import { CampaignAPI, SspAPI, SspDealAPI } from "generated-api"
import { checkIfSessionIsOrWillExpired } from "user/tools/common"
import { ICampaignSspDeal, ISsp, ISspAssociation, ISspDeal, ISspRepository } from "./model"

export const sspRPO: ISspRepository = {
  getAllSsps(): Promise<ISsp[]> {
    return new Promise<ISsp[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspAPI.getApiSspList()
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting all ssps", error)
            reject("ssp.get.all.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all ssps", ex)
        reject("ssp.get.all.error.unexpected")
      }
    })
  },
  getSspsByName(name: string): Promise<ISsp[]> {
    return new Promise<ISsp[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspAPI.getApiSspList(undefined, undefined, name, "contains")
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while searching ssps by name", error)
            reject("ssp.get.by.name.error.server")
          })
      } catch (ex) {
        console.error("Server error while searching ssps by name", ex)
        reject("ssp.get.by.name.error.unexpected")
      }
    })
  },
  getSspsById(ids: number[]): Promise<ISsp[]> {
    return new Promise<ISsp[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspAPI.getApiSspList(undefined, undefined, undefined, undefined, undefined, undefined, ids)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting ssps by ids", error)
            reject("ssp.get.by.ids.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting ssps by ids", ex)
        reject("ssp.get.by.ids.error.unexpected")
      }
    })
  },
  getCampaignSsps(campaignId: number): Promise<ISspAssociation[]> {
    return new Promise<ISspAssociation[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignSspList(campaignId)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while getting all ssp associations", error)
            reject("ssp.get.campaign.error.server")
          })
      } catch (ex) {
        console.error("Server error while getting all ssp associations", ex)
        reject("ssp.get.campaign.error.unexpected")
      }
    })
  },
  associateSsp(campaignId: number, sspId: number): Promise<ISspAssociation> {
    return new Promise<ISspAssociation>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignSsp({ campaign_id: campaignId, ssp_id: sspId })
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while associating ssp", error)
            reject("ssp.associate.error.server")
          })
      } catch (ex) {
        console.error("Server error while associating ssp", ex)
        reject("ssp.associate.error.unexpected")
      }
    })
  },
  setSsps(campaignId: number, sspIds: number[]): Promise<ISspAssociation[]> {
    return new Promise<ISspAssociation[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignSspList(
          campaignId,
          sspIds.map(ssp_id => ({ campaign_id: campaignId, ssp_id }))
        )
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while settings campaign ssps", error)
            reject("ssp.set.error.server")
          })
      } catch (ex) {
        console.error("Server error while settings campaign ssps", ex)
        reject("ssp.set.error.unexpected")
      }
    })
  },
  unassociateSsp(campaignId: number, associationId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.deleteApiCampaignSsp(campaignId, associationId)
          .then(() => resolve())
          .catch((error: any) => {
            console.error("Server error while unassociating ssp", error)
            reject("ssp.unassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while unassociating ssp", ex)
        reject("ssp.unassociate.error.unexpected")
      }
    })
  },
  getAllSspDeals(sspIds: number[]): Promise<ISspDeal[]> {
    return new Promise<ISspDeal[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        SspDealAPI.getApiSspDealList(
          undefined, // offset?: number,
          undefined, // limit?: number,
          undefined, // search?: string,
          undefined, // match: 'exact' | 'contains' | 'starts_with' | 'ends_with' | 'not' = 'starts_with',
          undefined, // orderby?: Array<string>,
          undefined, // comparator?: Array<string>,
          undefined, // dealid?: Array<string>,
          sspIds
        )
          .then(response => resolve(response.filter(s => !s.is_deleted)))
          .catch((error: any) => {
            console.error("Server error while gettings ssp's dsp-deals", error)
            reject("ssp.unassociate.error.server")
          })
      } catch (ex) {
        console.error("Server error while gettings ssp's dsp-deals", ex)
        reject("ssp.unassociate.error.unexpected")
      }
    })
  },
  getCampaignSspDeals(campaignId: number): Promise<ICampaignSspDeal[]> {
    return new Promise<ICampaignSspDeal[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.getApiCampaignSspDealList(campaignId)
          .then(response => resolve(response.filter(s => s.status_id === "active")))
          .catch((error: any) => {
            console.error("Server error while getting campaign ssp deals", error)
            reject("ssp.get.dsp-deals.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while getting campaign ssp deals", ex)
        reject("ssp.get.dsp-deals.error.unexpected")
      }
    })
  },
  setCampaignSspDeals(campaignId: number, sspDeals: ICampaignSspDeal[]): Promise<ICampaignSspDeal[]> {
    return new Promise<ICampaignSspDeal[]>((resolve, reject) => {
      try {
        checkIfSessionIsOrWillExpired()
        CampaignAPI.postApiCampaignSspDealList(campaignId, sspDeals)
          .then(response => resolve(response))
          .catch((error: any) => {
            console.error("Server error while setting campaign ssp deals", error)
            reject("ssp.set.dsp-deals.error.server")
          })
      } catch (ex) {
        console.error("Unexpected error while setting campaign ssp deals", ex)
        reject("ssp.set.dsp-deals.error.unexpected")
      }
    })
  }
}
